import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import EventDetailDialog from "@/components/EventDetailDialog";
import GallerySlideshow from "@/components/GallerySlideshow";
import WinnersWall from "@/components/WinnersWall";
import InstagramFeed from "@/components/InstagramFeed";
import FeaturedEvent from "@/components/FeaturedEvent";
import UpcomingRacesStrip from "@/components/UpcomingRacesStrip";
import { ArrowRight, Calendar, Trophy, Flame } from "lucide-react";

export default function Landing() {
  const [stats, setStats] = useState({ runners: 0, events: 0, challenges: 0, total_km: 0 });
  const [events, setEvents] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [topRunners, setTopRunners] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    const fetchAll = () => {
      api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => {});
      api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
      api.get("/challenges").then((r) => setChallenges((r.data || []).filter((c) => c.status !== "past").slice(0, 3))).catch(() => {});
      api.get("/leaderboard").then((r) => setTopRunners(r.data.slice(0, 5))).catch(() => {});
      api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    };
    fetchAll();
    const onFocus = () => fetchAll();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") fetchAll();
    });
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Split events: upcoming (date >= today, end-of-day) vs past
  const now = Date.now();
  const upcomingEvents = events.filter((e) => new Date(e.date).getTime() >= now - 6 * 60 * 60 * 1000).slice(0, 3);
  const pastEvents = events
    .filter((e) => new Date(e.date).getTime() < now - 6 * 60 * 60 * 1000)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  return (
    <div data-testid="landing-page">
      <SEO path="/" />
      {/* TOP STATUS BAR — premium feel */}
      <div className="border-b border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-2 flex items-center justify-between text-[10px] font-mono tracking-[0.3em] text-zinc-500 uppercase">
          <span className="hidden sm:inline">EST · ROURKELA · STEEL CITY OF INDIA</span>
          <span className="sm:hidden">EST · ROURKELA</span>
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
            ACCEPTING NEW RUNNERS
          </span>
          <span className="hidden md:inline">FOUNDER · 2× GUINNESS WORLD RECORDS</span>
        </div>
      </div>

      {/* HERO — editorial cover */}
      <section className="hero-bg grain min-h-[92vh] flex items-end relative overflow-hidden">
        <div className="absolute inset-0 cover-breathe pointer-events-none hero-bg" aria-hidden="true" />
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            {/* LEFT COLUMN — copy + CTAs */}
            <div className={(settings.gallery_images || []).length > 0 ? "lg:col-span-7" : "lg:col-span-12"}>
              <div className="inline-flex items-center gap-2 px-3 py-1 hairline mb-6 bg-zinc-950/60 editorial-lift">
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
                <span className="font-mono text-[11px] tracking-[0.25em] text-zinc-300 uppercase">
                  BORN IN ROURKELA · BUILT FOR ALL OF ODISHA
                </span>
              </div>
              <h1 className="font-editorial text-6xl sm:text-7xl lg:text-[9.5rem] text-white leading-[0.86] max-w-5xl editorial-lift editorial-lift-delay-1">
                Run further.<br />
                <span className="text-orange-500">Together.</span>
              </h1>
              <p className="mt-8 text-zinc-300 text-base sm:text-lg max-w-2xl tracking-wide editorial-lift editorial-lift-delay-2">
                One state. One pack. From Rourkela&apos;s steel streets to Puri&apos;s dawn shoreline — and every village,
                town and city in between. Train in person or log your miles virtually. Whether you&apos;re chasing
                your first 5K or your hundredth ultra, you belong here.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 editorial-lift editorial-lift-delay-3">
                <Link to="/register">
                  <Button data-testid="hero-join-btn" className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider text-base px-6 py-6 group">
                    JOIN THE PACK
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition" size={18} />
                  </Button>
                </Link>
                <Link to="/events">
                  <Button variant="outline" className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider px-6 py-6">
                    SEE UPCOMING RUNS
                  </Button>
                </Link>
              </div>
            </div>

            {/* RIGHT COLUMN — community slideshow (only when photos exist) */}
            {(settings.gallery_images || []).length > 0 && (
              <div className="lg:col-span-5 editorial-lift editorial-lift-delay-2" data-testid="hero-slideshow-wrapper">
                <GallerySlideshow images={settings.gallery_images} variant="hero" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED EVENT SPOTLIGHT — admin-managed (Admin → Settings) */}
      <FeaturedEvent event={settings.featured_event} />

      {/* STATS BAR — elite oversized */}
      <section className="border-y border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-800">
          {[
            { k: "RUNNERS", v: stats.runners },
            { k: "EVENTS", v: stats.events },
            { k: "CHALLENGES", v: stats.challenges },
            { k: "TOTAL KM", v: stats.total_km },
          ].map((s, i) => (
            <div key={s.k} className="px-6 py-10 group hover:bg-zinc-900/40 transition">
              <div className="font-mono text-[10px] text-zinc-500 tracking-[0.3em]">0{i+1} / 04</div>
              <div className="font-display text-5xl sm:text-6xl text-white mt-3 leading-none group-hover:text-orange-500 transition">
                {s.v}
              </div>
              <div className="font-mono text-[10px] text-zinc-400 tracking-[0.3em] mt-3">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* DISTRICTS STRIP — Pan-Odisha unity */}
      <section className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <p className="font-mono text-[11px] tracking-[0.3em] text-zinc-500 uppercase text-center">
            RUNNERS JOINING FROM
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {[
              "ROURKELA","BHUBANESWAR","CUTTACK","PURI","SAMBALPUR","BERHAMPUR",
              "BALASORE","BARGARH","ANGUL","KORAPUT","JHARSUGUDA","SUNDARGARH",
              "KEONJHAR","DHENKANAL","JEYPORE","+ YOU"
            ].map((d, i) => (
              <span key={d} className={`font-display text-sm tracking-[0.2em] ${d === "+ YOU" ? "text-orange-500" : "text-zinc-400"}`}>
                {d}{i < 15 ? <span className="text-zinc-700 ml-6">·</span> : null}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US — Bento */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">
              § 01 / WHAT WE OFFER
            </p>
            <div className="hidden lg:block mt-3 w-12 h-0.5 bg-orange-500" />
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              One Odisha. <span className="text-orange-500">One running hub.</span>
            </h2>
            <p className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mt-4">
              In-person meets · Virtual check-ins · Every district welcome
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { t: "EVENTS", d: "Long runs and races across Odisha — Bhubaneswar, Cuttack, Sambalpur, Puri, Rourkela. RSVP and meet your district pack." , img: "https://images.pexels.com/photos/32944995/pexels-photo-32944995.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940", to: "/events"},
            { t: "CHALLENGES", d: "Run from anywhere — in-person or virtual. Daily check-ins, photo proof, Strava links. Earn Odia-soul badges like Konark Wheel and Mahanadi Mile.", img: "https://images.pexels.com/photos/31308316/pexels-photo-31308316.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940", to: "/challenges"},
            { t: "LEADERBOARD", d: "Every runner counted. From a Berhampur beginner to a Balasore ultra-marathoner, climb the same statewide board.", img: "https://images.pexels.com/photos/28506132/pexels-photo-28506132.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940", to: "/leaderboard"},
          ].map((f) => (
            <Link key={f.t} to={f.to} data-testid={`bento-${f.t.toLowerCase()}`} className="group hairline bg-zinc-900 hover:-translate-y-1 hover:border-orange-600/50 transition-all duration-300 relative overflow-hidden cursor-pointer">
              <div className="aspect-[4/3] bg-zinc-800 overflow-hidden">
                <img src={f.img} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition duration-500" />
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl text-white group-hover:text-orange-500 transition">{f.t}</h3>
                <p className="mt-2 text-sm text-zinc-400">{f.d}</p>
                <div className="mt-4 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase opacity-0 group-hover:opacity-100 transition">
                  EXPLORE <ArrowRight size={12} className="group-hover:translate-x-1 transition" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHERE WE'RE HEADING — upcoming races the pack is travelling to */}
      <UpcomingRacesStrip />

      {/* CHAMPIONS / WINNERS announcement */}
      <WinnersWall />

      {/* INSTAGRAM — curated feed from OSCR account */}
      <InstagramFeed
        posts={settings.instagram_posts || []}
        username={settings.instagram_oscr}
      />

      {/* Upcoming events preview */}
      {upcomingEvents.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">
                ON THE CALENDAR
              </p>
              <h2 className="font-display text-3xl sm:text-4xl text-white mt-1">Upcoming runs</h2>
            </div>
            <Link to="/events" className="font-display text-sm tracking-wider text-orange-500 hover:text-orange-400">
              ALL EVENTS →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {upcomingEvents.map((e) => (
              <EventDetailDialog key={e.event_id} event={e}>
                <button
                  type="button"
                  data-testid={`landing-event-${e.event_id}`}
                  className="text-left w-full group hairline bg-zinc-900 p-6 hover:-translate-y-1 hover:border-orange-600/50 transition-all cursor-pointer"
                >
                  <p className="font-mono text-[10px] tracking-[0.25em] text-orange-500 uppercase">
                    {new Date(e.date).toDateString()}
                  </p>
                  <h3 className="font-display text-xl text-white mt-2 group-hover:text-orange-400 transition">{e.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1">{e.location}</p>
                  {e.distance_km && (
                    <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-2">
                      {e.distance_km} KM
                    </p>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase opacity-0 group-hover:opacity-100 transition">
                    VIEW DETAILS <ArrowRight size={12} />
                  </div>
                </button>
              </EventDetailDialog>
            ))}
          </div>
        </section>
      )}

      {/* Past events archive */}
      {pastEvents.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="past-events-section">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-zinc-500 uppercase">
                IN THE ARCHIVES
              </p>
              <h2 className="font-display text-3xl sm:text-4xl text-zinc-300 mt-1">Past runs</h2>
            </div>
            <Link to="/events" className="font-display text-sm tracking-wider text-zinc-400 hover:text-orange-400">
              SEE ALL →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pastEvents.map((e) => (
              <EventDetailDialog key={e.event_id} event={e}>
                <button
                  type="button"
                  data-testid={`past-event-${e.event_id}`}
                  className="text-left w-full group hairline bg-zinc-950 border-zinc-800 p-6 opacity-80 hover:opacity-100 hover:border-zinc-700 transition cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
                      {new Date(e.date).toDateString()}
                    </p>
                    <span className="font-mono text-[8px] tracking-[0.25em] uppercase px-1.5 py-0.5 hairline border-zinc-700 text-zinc-500">
                      PAST
                    </span>
                  </div>
                  <h3 className="font-display text-xl text-zinc-300 mt-2 group-hover:text-white transition">{e.title}</h3>
                  <p className="text-sm text-zinc-500 mt-1">{e.location}</p>
                  {(e.rsvps?.length || 0) > 0 && (
                    <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-2">
                      {e.rsvps.length} RUNNERS WENT
                    </p>
                  )}
                </button>
              </EventDetailDialog>
            ))}
          </div>
        </section>
      )}

      {/* LIVE: Active challenges + Top runners */}
      {(challenges.length > 0 || topRunners.length > 0) && (
        <section className="border-t border-zinc-800 bg-zinc-950">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Active challenges - left 3 cols */}
            <div className="lg:col-span-3" data-testid="landing-challenges-section">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                  <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">LIVE NOW</p>
                  <h2 className="font-display text-3xl sm:text-4xl text-white mt-1">
                    Active <span className="text-orange-500">challenges.</span>
                  </h2>
                </div>
                <Link to="/challenges" className="font-display text-sm tracking-wider text-orange-500 hover:text-orange-400 inline-flex items-center gap-1">
                  ALL CHALLENGES <ArrowRight size={14} />
                </Link>
              </div>
              {challenges.length === 0 ? (
                <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm">
                  New challenges drop weekly. Stay tuned.
                </div>
              ) : (
                <div className="space-y-3">
                  {challenges.map((c) => (
                    <Link
                      key={c.challenge_id}
                      to={`/challenges/${c.challenge_id}`}
                      data-testid={`landing-challenge-${c.challenge_id}`}
                      className="group block hairline bg-zinc-900 p-5 hover:-translate-y-0.5 hover:border-orange-600/50 transition-all"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-[10px] tracking-[0.25em] text-orange-500 uppercase">
                            {c.start_date} → {c.end_date}
                          </p>
                          <h3 className="font-display text-xl text-white mt-1 group-hover:text-orange-400 transition">{c.title}</h3>
                          <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{c.description}</p>
                          <div className="mt-3 flex gap-4 text-xs font-mono text-zinc-400">
                            {c.target_days && <span><Calendar size={11} className="inline mr-1" />{c.target_days} DAYS</span>}
                            {c.target_km_per_day && <span><Flame size={11} className="inline mr-1" />{c.target_km_per_day} KM/DAY</span>}
                          </div>
                        </div>
                        <ArrowRight className="text-zinc-600 group-hover:text-orange-500 group-hover:translate-x-1 transition shrink-0 mt-1" size={20} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Top runners - right 2 cols */}
            <div className="lg:col-span-2" data-testid="landing-leaderboard-section">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                  <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">PACK LEADERS</p>
                  <h2 className="font-display text-3xl sm:text-4xl text-white mt-1">
                    Top <span className="text-orange-500">runners.</span>
                  </h2>
                </div>
              </div>
              <Link
                to="/leaderboard"
                data-testid="landing-leaderboard-link"
                className="block hairline bg-zinc-900 divide-y divide-zinc-800 group hover:border-orange-600/50 transition"
              >
                {topRunners.length === 0 ? (
                  <div className="p-6 text-center text-zinc-500 font-mono text-sm">Be the first to log a run</div>
                ) : topRunners.map((r, i) => (
                  <div key={r.user_id} className="p-4 flex items-center gap-3 group-hover:bg-zinc-900/80 transition">
                    <span className={`font-display text-2xl w-8 ${i === 0 ? "text-orange-500" : i === 1 ? "text-zinc-300" : i === 2 ? "text-amber-600" : "text-zinc-600"}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate font-display tracking-wide">{r.name}</p>
                      <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">
                        {r.city || "Odisha"} · {r.total_km || 0} KM
                      </p>
                    </div>
                    {i === 0 && <Trophy size={16} className="text-orange-500 shrink-0" />}
                  </div>
                ))}
                <div className="p-4 text-center font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase group-hover:bg-orange-600/10 transition">
                  VIEW FULL BOARD →
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FOUNDER */}
      <section className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] hairline bg-zinc-900 overflow-hidden">
              <img
                src={process.env.REACT_APP_FOUNDER_PHOTO || "https://images.pexels.com/photos/4761779/pexels-photo-4761779.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940"}
                alt="Sumit Kumar Singh — Founder, OSCR"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 hairline bg-orange-600 px-4 py-3 hidden sm:block">
              <p className="font-mono text-[10px] tracking-[0.25em] text-white/80 uppercase">2× GUINNESS</p>
              <p className="font-display text-xl text-white leading-none mt-1">WORLD RECORDS</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">
              FOUNDER · OSCR
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-3 leading-[0.95]">
              SUMIT KUMAR<br /><span className="text-orange-500">SINGH.</span>
            </h2>
            <p className="text-zinc-300 mt-5 max-w-2xl leading-relaxed">
              Ultra runner from Rourkela. Two-time Guinness World Record holder — <span className="text-white font-semibold">68.04 km on a MANUAL treadmill in 12 hours</span> (March 2024)
              and <span className="text-white font-semibold">201.1 km on a MANUAL treadmill in 48 hours</span> (December 2025, Civic Center, Rourkela).
              Also certified by the International Book of Records for running <span className="text-white font-semibold">33 marathons in 30 days</span> —
              1,392.6 km at Uditnagar Stadium.
            </p>
            <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed italic">
              "My vision is to make Odisha the endurance capital of India. Fitness for all. A healthier
              lifestyle for every household — one runner, one street, one steel city at a time."
            </p>
            <a
              href="https://www.instagram.com/sumitsinghultrarunner"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-instagram-link"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 hairline bg-zinc-900 hover:bg-orange-600 hover:border-orange-600 transition group"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500 group-hover:text-white transition">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
              <span className="font-display text-xs tracking-[0.2em] uppercase text-white">
                @sumitsinghultrarunner
              </span>
            </a>
            <div className="mt-7 grid grid-cols-3 gap-3 max-w-xl">
          {[
            { v: "68.04", u: "KM / 12 HR · MANUAL TREADMILL" },
            { v: "201.1", u: "KM / 48 HR · MANUAL TREADMILL" },
            { v: "33", u: "MARATHONS / 30 DAYS" },
          ].map((s) => (
                <div key={s.u} className="hairline bg-zinc-900 p-4">
                  <div className="font-display text-2xl sm:text-3xl text-orange-500">{s.v}</div>
                  <div className="font-mono text-[9px] text-zinc-500 tracking-[0.2em] mt-1">{s.u}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRESS / RECORDS STRIP */}
      <section className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <p className="font-mono text-[11px] tracking-[0.3em] text-zinc-500 uppercase text-center">
            AS COVERED BY
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { n: "GUINNESS", s: "World Records" },
              { n: "INDIA TODAY", s: "Trending" },
              { n: "TIMES OF INDIA", s: "Bhubaneswar" },
              { n: "CNN-NEWS18", s: "Feature" },
              { n: "SAIL OFFICIAL", s: "Steel of India" },
              { n: "COMRADES", s: "Marathon 2024" },
            ].map((p) => (
              <div key={p.n} className="hairline bg-zinc-900 px-4 py-5 text-center hover:border-orange-600/50 transition">
                <p className="font-display text-base lg:text-lg text-white tracking-wider">{p.n}</p>
                <p className="font-mono text-[9px] text-zinc-500 tracking-[0.2em] uppercase mt-1">{p.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BADGES */}
      <section className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <h2 className="font-display text-4xl sm:text-5xl text-white">
              Badges, <span className="text-orange-500">Odia soul.</span>
            </h2>
            <p className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase max-w-sm">
              Unlock identity, not just numbers
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
            {[
              { t: "KONARK WHEEL", d: "30-day streak", tier: "common" },
              { t: "MAHANADI MILE", d: "100 km logged", tier: "common" },
              { t: "PURI SUNRISE", d: "5 dawn (pre-6am) runs", tier: "common" },
              { t: "HIRAKUD HUNDRED", d: "First 100 km in a month", tier: "common" },
              { t: "JAGANNATH JOURNEY", d: "Pilgrimage long run", tier: "common" },
              { t: "STEEL CITY GRIT", d: "Complete any challenge", tier: "common" },
              { t: "CHILIKA CENTURY", d: "100 km in a single week", tier: "rare" },
              { t: "DAITARI DOUBLE", d: "200 km in a single month", tier: "rare" },
              { t: "MAHENDRAGIRI ELITE", d: "300 km in a single month", tier: "legend" },
              { t: "CUTTACK STRIDE", d: "50 km walked in a week", tier: "rare", walk: true },
              { t: "SAMBALPUR SAUNTER", d: "100 km walked in a month", tier: "rare", walk: true },
              { t: "PIPILI PILGRIM", d: "200 km walked in a month", tier: "legend", walk: true },
            ].map((b) => (
              <div key={b.t} className={`hairline bg-zinc-900 p-5 hover:-translate-y-1 transition group ${
                b.tier === "legend" ? "border-orange-600/40 hover:border-orange-500"
                : b.tier === "rare" ? "border-zinc-700 hover:border-orange-600/40"
                : "hover:border-orange-600/30"
              }`}>
                <div className={`w-10 h-10 hairline flex items-center justify-center mb-3 transition group-hover:scale-110 ${
                  b.tier === "legend" ? "bg-orange-600 border-orange-500"
                  : b.tier === "rare" ? "bg-orange-600/20 border-orange-600/60"
                  : "bg-orange-600/10 border-orange-600/40"
                }`}>
                  <span className={`font-display text-lg ${b.tier === "legend" ? "text-white" : "text-orange-500"}`}>★</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-display text-base text-white tracking-wider">{b.t}</h4>
                  {b.walk && (
                    <span className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 hairline border-zinc-700 text-zinc-400">WALK</span>
                  )}
                  {b.tier === "legend" && (
                    <span className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-orange-600 text-white">LEGEND</span>
                  )}
                  {b.tier === "rare" && (
                    <span className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 hairline border-orange-600/60 text-orange-500">RARE</span>
                  )}
                </div>
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-3xl sm:text-4xl text-white max-w-2xl">
              From every district, <span className="text-orange-500">we run as one.</span>
            </h3>
            <p className="text-zinc-400 mt-2 max-w-xl">
              Join in person or virtually. Whether you're in Rourkela, Bhubaneswar, Berhampur,
              Sambalpur, Cuttack, Puri — or anywhere in the world running for Odisha.
            </p>
          </div>
          <Link to="/register">
            <Button data-testid="bottom-cta-btn" className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider text-base px-6 py-6">
              CREATE FREE ACCOUNT
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
